import { Canvas, useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Color, type DirectionalLight, type Group } from 'three';
import type { Building, RegionNode, RegionTree, Task } from '@map/shared';
import type { Territory, WorldArt } from '@map/worldkit';
import { prefersReducedMotion, sceneColor, sceneHealthColor } from '../styles/tokens.js';
import { buildBorderGeometry, buildTerritoryGeometry } from './geometry.js';
import { orderSalahCountries } from './salahOrder.js';
import { useWorldLayout } from './useWorldLayout.js';
import { visibleTerritories } from './visible.js';
import { OrbitCamera } from './OrbitCamera.js';
import { LabelOverlay, LabelProjector, type PlacedLabel } from './Labels.js';
import { Camels } from './Camels.js';
import { People } from './People.js';
import { Rain } from './Rain.js';
import { ArabicLand } from './ArabicLand.js';
import { Medals } from './Medals.js';
import { Meditator } from './Meditator.js';
import { Boats } from './Boats.js';
import { SalahRiver } from './SalahRiver.js';
import { TerrainBuildings } from './TerrainBuildings.js';

export interface GlobeProps {
  art: WorldArt;
  tree: RegionTree;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** How deep to draw: continents only, or their children too. */
  depth: number;
  /** Every building on this planet, drawn on the ground it belongs to. */
  buildings?: Building[];
  /** This planet's tasks: the completed ones sail its ocean as boats. */
  tasks?: Task[];
  /** Regions the world had no room for, so nothing vanishes in silence. */
  onUnplaced?: (unplaced: Array<{ id: string; reason: string }>) => void;
  /** A camel was clicked: open its plot, and its surah inside it. */
  /** `itemId` picks one thing inside the building, e.g. a word in the forest. */
  onOpenBuilding?: (regionId: string, buildingId: string, itemId?: number) => void;
}

/** Resolves a health score to an actual colour, via the CSS tokens. */
function useHealthColours(): (score: number) => Color {
  return useMemo(() => {
    const cache = new Map<number, Color>();

    return (score: number) => {
      const bucket = Math.round(Math.min(1, Math.max(0, score)) * 40) / 40;
      const cached = cache.get(bucket);
      if (cached) return cached;

      const colour = new Color(sceneHealthColor(bucket));
      cache.set(bucket, colour);
      return colour;
    };
  }, []);
}

/**
 * The ground of every place you are inside, laid under whatever is drawn on it.
 *
 * Plots do not tile their city — there is open land between them by design — so
 * without this the gaps showed ocean through the floor and a city you had
 * opened went blue and patchy. Each level on the way down gets its own floor,
 * stacked in the order you entered them, so the land is green whether you are
 * looking at a country, a city or one building.
 */
function Ground({
  territory,
  colour,
  depth,
}: {
  territory: Territory;
  colour: Color;
  depth: number;
}) {
  const geometry = useMemo(
    // Stacked just under the territories themselves, which sit at 0.02, and
    // each level a hair above its parent so they cannot fight for the surface.
    () => buildTerritoryGeometry(territory.ring, { elevation: 0.017 + depth * 0.0007 }),
    [territory, depth],
  );
  useEffect(() => () => geometry?.dispose(), [geometry]);
  if (!geometry) return null;

  return (
    <mesh geometry={geometry} raycast={() => null}>
      <meshStandardMaterial flatShading color={colour} roughness={0.9} metalness={0} />
    </mesh>
  );
}

/**
 * The boundary of the place you have opened, kept on screen after its children
 * have replaced it. Without it there is nothing to say which of the shapes on
 * screen belong together, and a selection reads as a mess of small territories
 * rather than as the inside of one large one.
 */
function OpenBoundary({ territory }: { territory: Territory }) {
  const geometry = useMemo(() => buildBorderGeometry(territory.ring, 0.03), [territory]);
  useEffect(() => () => geometry.dispose(), [geometry]);
  return (
    <lineSegments geometry={geometry}>
      <lineBasicMaterial color="#ffffff" transparent opacity={0.85} />
    </lineSegments>
  );
}

function Territories({
  territories,
  tree,
  depth,
  selectedId,
  hoveredId,
  onSelect,
  onHover,
}: {
  territories: Map<string, Territory>;
  tree: RegionTree;
  depth: number;
  selectedId: string | null;
  hoveredId: string | null;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
}) {
  const colourFor = useHealthColours();

  const visible = useMemo(() => visibleTerritories(territories, tree, depth, selectedId), [
    territories,
    tree,
    depth,
    selectedId,
  ]);

  const geometries = useMemo(
    () =>
      visible.map(({ region, territory }) => ({
        id: region.id,
        geometry: buildTerritoryGeometry(territory.ring),
        border: buildBorderGeometry(territory.ring),
      })),
    [visible],
  );

  useEffect(
    () => () => {
      for (const entry of geometries) {
        entry.geometry?.dispose();
        entry.border.dispose();
      }
    },
    [geometries],
  );

  return (
    <group>
      {geometries.map((entry, index) => {
        const region = visible[index]?.region;
        if (!entry.geometry || !region) return null;

        const isSelected = region.id === selectedId;
        const isHovered = region.id === hoveredId;
        // Only the rest of the world steps back. Dimming the insides of the
        // place you just opened made it look faded and see-through, which is
        // the opposite of paying attention to it.
        const dimmed = selectedId !== null && !isSelected && region.parentId !== selectedId;

        return (
          <group key={entry.id}>
            <mesh
              geometry={entry.geometry}
              // Never lifted on hover or selection: raised even slightly, the
              // ground swallows the short buildings standing on it.
              onPointerOver={(e: ThreeEvent<PointerEvent>) => {
                e.stopPropagation();
                onHover(region.id);
              }}
              onPointerOut={() => onHover(null)}
              onClick={(e: ThreeEvent<MouseEvent>) => {
                e.stopPropagation();
                onSelect(region.id);
              }}
            >
              <meshStandardMaterial
                flatShading
                color={colourFor(region.health.combined)}
                roughness={0.9}
                metalness={0}
                emissive={colourFor(region.health.combined)}
                emissiveIntensity={isSelected ? 0.25 : isHovered ? 0.12 : 0}
                opacity={dimmed ? 0.75 : 1}
                transparent={dimmed}
              />
            </mesh>
            <lineSegments geometry={entry.border}>
              <lineBasicMaterial
                color={isSelected ? '#ffffff' : '#0d1b2a'}
                transparent
                opacity={isSelected ? 0.9 : 0.35}
              />
            </lineSegments>
          </group>
        );
      })}
    </group>
  );
}

function Ocean() {
  const colours = useMemo(
    () => ({
      deep: new Color(sceneColor('ocean-deep', '#1d3a5c')),
      atmosphere: new Color(sceneColor('atmosphere', '#86b6e0')),
    }),
    [],
  );

  return (
    <group>
      <mesh>
        <sphereGeometry args={[1, 96, 96]} />
        <meshStandardMaterial color={colours.deep} roughness={0.55} metalness={0.05} />
      </mesh>
      <mesh scale={1.06}>
        <sphereGeometry args={[1, 48, 48]} />
        <meshBasicMaterial color={colours.atmosphere} transparent opacity={0.08} side={1} />
      </mesh>
    </group>
  );
}

/**
 * A light on the camera.
 *
 * The two fixed suns are what give the planet its shape from orbit, but they
 * leave half of it in night — and once you are down on the surface, the place
 * you chose to look at is as likely to be on the dark side as not. Zooming in
 * to find an unlit field is not atmosphere, it is a fault. This fades in as
 * you approach so the overview keeps its shadows.
 */
function Headlight() {
  const light = useRef<DirectionalLight>(null);
  const { camera } = useThree();

  useFrame(() => {
    if (!light.current) return;
    const distance = camera.position.length();
    light.current.position.copy(camera.position);
    light.current.intensity = distance < 2.4 ? Math.min(1.1, (2.4 - distance) * 1.1) : 0;
  });

  return <directionalLight ref={light} intensity={0} />;
}

function Spin({
  enabled,
  angle,
  children,
}: {
  enabled: boolean;
  /** Written back each frame: the camera has to know how far the world has turned. */
  angle: React.MutableRefObject<number>;
  children: React.ReactNode;
}) {
  const group = useRef<Group>(null);
  useFrame((_, delta) => {
    if (!group.current) return;
    if (enabled) group.current.rotation.y += delta * 0.02;
    angle.current = group.current.rotation.y;
  });
  return <group ref={group}>{children}</group>;
}

/** Turns a point on the globe into where it has actually ended up on screen. */
function spun(site: { x: number; y: number; z: number }, angle: number) {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return { x: site.x * cos + site.z * sin, y: site.y, z: -site.x * sin + site.z * cos };
}

function SceneContents(
  props: GlobeProps & {
    hoveredId: string | null;
    onHover: (id: string | null) => void;
    onPlaceLabels: (labels: PlacedLabel[]) => void;
  },
) {
  const layout = useWorldLayout(props.art, props.tree);
  // The five prayers are drawn in prayer order along the ground; everything
  // else stays exactly where the partition put it.
  const territories = useMemo(
    () => (layout ? orderSalahCountries(layout.territories, props.buildings ?? []) : null),
    [layout, props.buildings],
  );
  const { invalidate } = useThree();
  const colourFor = useHealthColours();

  // Everywhere you are currently inside, outermost first: the selected place
  // and every place containing it.
  const grounds = useMemo(() => {
    if (!territories || !props.selectedId) return [];
    const chain: RegionNode[] = [];
    for (
      let node: RegionNode | undefined = props.tree.regions[props.selectedId];
      node;
      node = node.parentId ? props.tree.regions[node.parentId] : undefined
    ) {
      chain.unshift(node);
    }
    return chain
      .map((region) => ({ region, territory: territories.get(region.id) }))
      .filter((entry): entry is { region: RegionNode; territory: Territory } => entry.territory !== undefined);
  }, [territories, props.selectedId, props.tree]);

  useEffect(() => invalidate(), [layout, props.selectedId, props.hoveredId, invalidate]);

  const { onUnplaced } = props;
  useEffect(() => {
    if (layout) onUnplaced?.(layout.unplaced);
  }, [layout, onUnplaced]);

  // Where the land is, so the first view of a planet is not open ocean. This
  // is deliberately not affected by what is selected: the camera moving on its
  // own every time you click something is disorienting, and clicking a
  // building should never throw you across the planet.
  const spinAngle = useRef(0);
  const facing = useMemo(() => {
    if (!territories || territories.size === 0) return null;

    const sum = [...territories.values()]
      .filter((t) => t.level === 0)
      .reduce(
        (acc, t) => ({ x: acc.x + t.site.x, y: acc.y + t.site.y, z: acc.z + t.site.z }),
        { x: 0, y: 0, z: 0 },
      );
    const length = Math.hypot(sum.x, sum.y, sum.z);
    return length > 1e-6
      ? spun({ x: sum.x / length, y: sum.y / length, z: sum.z / length }, spinAngle.current)
      : null;
  }, [territories]);

  return (
    <>
      <OrbitCamera facing={facing} />
      <ambientLight intensity={0.4} />
      <Headlight />
      <directionalLight position={[4, 3, 5]} intensity={1.5} />
      <directionalLight position={[-5, -2, -3]} intensity={0.3} />
      <Spin enabled={!prefersReducedMotion() && props.selectedId === null} angle={spinAngle}>
        <Ocean />
        {territories && (
          <LabelProjector
            territories={territories}
            tree={props.tree}
            depth={props.depth}
            selectedId={props.selectedId}
            onPlace={props.onPlaceLabels}
          />
        )}

        {territories && (
          <TerrainBuildings
            territories={territories}
            tree={props.tree}
            buildings={props.buildings ?? []}
            selectedId={props.selectedId}
          />
        )}
        {territories && (
          <Camels
            territories={territories}
            tree={props.tree}
            buildings={props.buildings ?? []}
            selectedId={props.selectedId}
            {...(props.onOpenBuilding ? { onOpen: props.onOpenBuilding } : {})}
          />
        )}
        {territories && (
          <People
            territories={territories}
            tree={props.tree}
            buildings={props.buildings ?? []}
            selectedId={props.selectedId}
            {...(props.onOpenBuilding ? { onOpen: props.onOpenBuilding } : {})}
          />
        )}
        {territories && <Rain territories={territories} buildings={props.buildings ?? []} />}
        {territories && (
          <ArabicLand
            territories={territories}
            buildings={props.buildings ?? []}
            {...(props.onOpenBuilding ? { onOpenWord: props.onOpenBuilding } : {})}
          />
        )}
        {territories && (
          <Medals
            territories={territories}
            tree={props.tree}
            buildings={props.buildings ?? []}
            {...(props.onOpenBuilding ? { onOpen: props.onOpenBuilding } : {})}
          />
        )}
        {territories && (
          <Meditator
            territories={territories}
            buildings={props.buildings ?? []}
            {...(props.onOpenBuilding ? { onOpen: props.onOpenBuilding } : {})}
          />
        )}
        {territories && (
          <SalahRiver territories={territories} tree={props.tree} buildings={props.buildings ?? []} />
        )}
        {territories && <Boats territories={territories} tasks={props.tasks ?? []} />}
        {grounds.map(({ region, territory }, depth) => (
          <Ground
            key={region.id}
            territory={territory}
            colour={colourFor(region.health.combined)}
            depth={depth}
          />
        ))}
        {territories && props.selectedId && territories.get(props.selectedId) && (
          <OpenBoundary territory={territories.get(props.selectedId) as Territory} />
        )}
        {territories && (
          <Territories
            territories={territories}
            tree={props.tree}
            depth={props.depth}
            selectedId={props.selectedId}
            hoveredId={props.hoveredId}
            onSelect={props.onSelect}
            onHover={props.onHover}
          />
        )}
      </Spin>
    </>
  );
}

/** How long after opening a camel a click is still that camel's. */
const CAMEL_CLICK_MS = 250;

export function Globe(props: GlobeProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [labels, setLabels] = useState<PlacedLabel[]>([]);

  /**
   * When a camel was last opened.
   *
   * A camel is picked from raw pointer events, because every camel is one
   * instance of a shared mesh and a ray never finds it. The very same click
   * then reaches react-three-fiber, which looks for what is under it and acts
   * on that, throwing away the selection the camel had just made. Where the
   * camel stands over ocean or a plot too deep to be drawn, that is
   * onPointerMissed; where it stands on a continent that is drawn — any
   * continent but the one already open — it is a plain click on that
   * continent, which selects it and clears the building the camel had just
   * put in the panel. Either way the surah never opens.
   *
   * So a click that opened a camel is spent: nothing else may act on it.
   */
  const camelOpened = useRef(0);
  const spentOnCamel = () => performance.now() - camelOpened.current < CAMEL_CLICK_MS;
  const { onOpenBuilding, onSelect } = props;

  return (
    <div className="relative h-full w-full">
    <Canvas
      camera={{ position: [0, 0.6, 3], fov: 40 }}
      dpr={[1, 2]}
      gl={{ antialias: true }}
      onPointerMissed={() => {
        // The click that opened a camel is not a click on nothing.
        if (spentOnCamel()) return;
        onSelect(null);
      }}
      style={{ cursor: hoveredId ? 'pointer' : 'grab' }}
    >
      <SceneContents
        {...props}
        onSelect={(id) => {
          if (spentOnCamel()) return;
          onSelect(id);
        }}
        {...(onOpenBuilding
          ? {
              onOpenBuilding: (regionId: string, buildingId: string, itemId?: number) => {
                camelOpened.current = performance.now();
                onOpenBuilding(regionId, buildingId, itemId);
              },
            }
          : {})}
        hoveredId={hoveredId}
        onHover={setHoveredId}
        onPlaceLabels={setLabels}
      />
    </Canvas>
    <LabelOverlay labels={labels} />
    </div>
  );
}
